import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  CreditCard, Check, AlertCircle, Clock, ArrowUpRight, Shield, 
  Trash2, Plus, Loader2, AlertTriangle 
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Progress } from "../../components/ui/progress";
import { toast } from "sonner";
import axios from "axios";
import { API } from "../../App";
import { loadStripe } from "@stripe/stripe-js";
import { Elements, CardElement, useStripe, useElements } from "@stripe/react-stripe-js";

// Get Stripe public key from environment
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

// Card input styling
const CARD_ELEMENT_OPTIONS = {
  style: {
    base: {
      color: "#fff",
      fontFamily: '"Inter", system-ui, sans-serif',
      fontSmoothing: "antialiased",
      fontSize: "16px",
      "::placeholder": {
        color: "#6b7280"
      },
      backgroundColor: "transparent"
    },
    invalid: {
      color: "#ef4444",
      iconColor: "#ef4444"
    }
  }
};

// Payment Method Form Component
const PaymentMethodForm = ({ onSuccess, onCancel }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!stripe || !elements) {
      return;
    }

    setSaving(true);
    setError(null);

    try {
      // Create setup intent on backend
      const { data: setupData } = await axios.post(
        `${API}/billing/setup-intent`,
        {},
        { withCredentials: true }
      );

      // Confirm card setup with Stripe
      const { error: stripeError, setupIntent } = await stripe.confirmCardSetup(
        setupData.client_secret,
        {
          payment_method: {
            card: elements.getElement(CardElement)
          }
        }
      );

      if (stripeError) {
        setError(stripeError.message);
        setSaving(false);
        return;
      }

      // Confirm setup on backend
      const { data: confirmData } = await axios.post(
        `${API}/billing/confirm-setup`,
        { setup_intent_id: setupIntent.id },
        { withCredentials: true }
      );

      toast.success("Payment method saved successfully!");
      onSuccess(confirmData.payment_method);
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to save payment method");
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="p-4 bg-background/50 border border-border rounded-sm">
        <CardElement options={CARD_ELEMENT_OPTIONS} />
      </div>
      
      {error && (
        <div className="flex items-center gap-2 text-red-400 text-sm">
          <AlertCircle className="w-4 h-4" />
          {error}
        </div>
      )}
      
      <div className="flex gap-3">
        <Button
          type="submit"
          disabled={!stripe || saving}
          className="btn-primary"
          data-testid="save-card-btn"
        >
          {saving ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <CreditCard className="w-4 h-4 mr-2" />
              Save Card
            </>
          )}
        </Button>
        <Button type="button" variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </form>
  );
};

// Main Billing Page Component
const BillingPage = () => {
  const [subscription, setSubscription] = useState(null);
  const [planContract, setPlanContract] = useState(null);
  const [accountStatus, setAccountStatus] = useState(null);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [features, setFeatures] = useState({});
  const [loading, setLoading] = useState(true);
  const [canceling, setCanceling] = useState(false);
  const [showAddCard, setShowAddCard] = useState(false);
  const [deletingCard, setDeletingCard] = useState(null);
  const [payingPastDue, setPayingPastDue] = useState(false);
  const [reactivating, setReactivating] = useState(false);

  const fetchBillingData = useCallback(async () => {
    try {
      // Fetch all billing data with individual error handling
      const [subRes, contractRes, featRes, pmRes, statusRes] = await Promise.allSettled([
        axios.get(`${API}/billing/subscription`, { withCredentials: true }),
        axios.get(`${API}/billing/subscription/plan-contract`, { withCredentials: true }),
        axios.get(`${API}/billing/features`, { withCredentials: true }),
        axios.get(`${API}/billing/payment-methods`, { withCredentials: true }),
        axios.get(`${API}/billing/account-status`, { withCredentials: true })
      ]);
      
      // Handle subscription (primary source)
      if (subRes.status === 'fulfilled') {
        setSubscription(subRes.value.data);
      } else {
        console.error("Failed to fetch subscription:", subRes.reason);
      }
      
      // Handle plan contract (authoritative plan source)
      if (contractRes.status === 'fulfilled') {
        setPlanContract(contractRes.value.data);
      } else {
        console.error("Failed to fetch plan contract:", contractRes.reason);
      }
      
      // Handle features
      if (featRes.status === 'fulfilled') {
        setFeatures(featRes.value.data.features || {});
      } else {
        console.error("Failed to fetch features:", featRes.reason);
        setFeatures({});
      }
      
      // Handle payment methods
      if (pmRes.status === 'fulfilled') {
        setPaymentMethods(pmRes.value.data.payment_methods || []);
      } else {
        console.error("Failed to fetch payment methods:", pmRes.reason);
        setPaymentMethods([]);
      }
      
      // Handle account status
      if (statusRes.status === 'fulfilled') {
        setAccountStatus(statusRes.value.data);
      } else {
        console.error("Failed to fetch account status:", statusRes.reason);
      }
      
      // Show error toast only if critical data failed
      const criticalFailed = subRes.status === 'rejected' && contractRes.status === 'rejected';
      if (criticalFailed) {
        toast.error("Failed to load billing information");
      }
    } catch (error) {
      console.error("Unexpected error fetching billing data:", error);
      toast.error("Failed to load billing information");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBillingData();
  }, [fetchBillingData]);

  const handleCancelSubscription = async () => {
    if (!window.confirm("Are you sure you want to cancel your subscription? You'll continue to have access until the end of your billing period.")) {
      return;
    }

    setCanceling(true);
    try {
      await axios.post(`${API}/billing/cancel`, {}, { withCredentials: true });
      toast.success("Subscription will cancel at period end");
      fetchBillingData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to cancel subscription");
    } finally {
      setCanceling(false);
    }
  };

  const handleReactivateSubscription = async () => {
    setReactivating(true);
    try {
      await axios.post(`${API}/billing/reactivate`, {}, { withCredentials: true });
      toast.success("Subscription reactivated! You will continue to be billed.");
      fetchBillingData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to reactivate subscription");
    } finally {
      setReactivating(false);
    }
  };

  const handleSetDefaultCard = async (paymentMethodId) => {
    try {
      await axios.post(
        `${API}/billing/payment-methods/${paymentMethodId}/default`,
        {},
        { withCredentials: true }
      );
      toast.success("Default payment method updated");
      fetchBillingData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update payment method");
    }
  };

  const handleDeleteCard = async (paymentMethodId) => {
    if (!window.confirm("Are you sure you want to remove this payment method?")) {
      return;
    }

    setDeletingCard(paymentMethodId);
    try {
      await axios.delete(
        `${API}/billing/payment-methods/${paymentMethodId}`,
        { withCredentials: true }
      );
      toast.success("Payment method removed");
      fetchBillingData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to remove payment method");
    } finally {
      setDeletingCard(null);
    }
  };

  const handlePayPastDue = async () => {
    setPayingPastDue(true);
    try {
      const { data } = await axios.post(
        `${API}/billing/pay-past-due`,
        {},
        { withCredentials: true }
      );
      toast.success(data.message);
      fetchBillingData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Payment failed");
    } finally {
      setPayingPastDue(false);
    }
  };

  const handleCardAdded = (newCard) => {
    setShowAddCard(false);
    fetchBillingData();
  };

  const getStatusBadge = (status) => {
    const badges = {
      trialing: { label: "Trial", className: "bg-blue-500/20 text-blue-400 border-blue-500/30" },
      active: { label: "Active", className: "bg-green-500/20 text-green-400 border-green-500/30" },
      past_due: { label: "Past Due", className: "bg-red-500/20 text-red-400 border-red-500/30" },
      canceled: { label: "Canceled", className: "bg-gray-500/20 text-gray-400 border-gray-500/30" },
      suspended: { label: "Suspended", className: "bg-red-500/20 text-red-400 border-red-500/30" }
    };
    return badges[status] || badges.trialing;
  };

  const getCardBrandIcon = (brand) => {
    const brands = {
      visa: "VISA",
      mastercard: "MC",
      amex: "AMEX",
      discover: "DISC"
    };
    return brands[brand?.toLowerCase()] || brand?.toUpperCase() || "CARD";
  };

  // Check for pending tier changes
  const hasPendingChange = () => {
    const tierChangeState = subscription?.tier_change_state;
    return tierChangeState && tierChangeState !== 'no_change' && tierChangeState !== 'NO_CHANGE';
  };

  const getPendingChangeInfo = () => {
    if (!hasPendingChange()) return null;
    
    const state = subscription?.tier_change_state;
    if (state === 'upgrade_pending_payment' || state === 'UPGRADE_PENDING_PAYMENT') {
      return { type: 'upgrade', message: 'Upgrade pending payment confirmation' };
    }
    if (state === 'downgrade_pending' || state === 'DOWNGRADE_PENDING') {
      const pendingTier = subscription?.pending_tier;
      return { 
        type: 'downgrade', 
        message: `Downgrade to ${pendingTier?.charAt(0).toUpperCase() + pendingTier?.slice(1)} scheduled`,
        pendingTier 
      };
    }
    return null;
  };

  // Get current plan display info from plan contract (authoritative source)
  const getCurrentPlanDisplay = () => {
    if (planContract) {
      return {
        name: planContract.name || planContract.tier?.charAt(0).toUpperCase() + planContract.tier?.slice(1) || "Unknown",
        price: planContract.monthly_price ?? 0,
        tier: planContract.tier
      };
    }
    // Fallback to subscription data
    return {
      name: subscription?.current_plan?.name || "Starter",
      price: subscription?.current_plan?.monthly_price || 19,
      tier: subscription?.current_plan?.key || "starter"
    };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const statusBadge = getStatusBadge(subscription?.subscription_status);
  const trialProgress = subscription?.trial_days_remaining 
    ? ((14 - subscription.trial_days_remaining) / 14) * 100 
    : 100;
  
  const isRestricted = accountStatus?.account_status === "restricted";
  const isSuspended = accountStatus?.account_status === "suspended";

  return (
    <div className="space-y-8 max-w-4xl" data-testid="billing-page">
      <div>
        <h1 className="text-3xl md:text-4xl tracking-tight mb-2">Billing & Subscription</h1>
        <p className="text-muted-foreground">Manage your subscription and billing settings</p>
      </div>

      {/* Account Restriction Banner */}
      {isRestricted && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="p-4 bg-red-500/10 border border-red-500/30 rounded-sm"
        >
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-6 h-6 text-red-400 shrink-0 mt-0.5" />
            <div className="flex-1">
              <h3 className="font-semibold text-red-400 mb-1">Account Access Restricted</h3>
              <p className="text-sm text-muted-foreground mb-3">
                Your subscription is past due. You have limited access to your dashboard. 
                {accountStatus?.days_until_suspension !== null && (
                  <span className="font-medium text-red-400">
                    {" "}Your account will be suspended in {accountStatus.days_until_suspension} days.
                  </span>
                )}
              </p>
              <Button
                onClick={handlePayPastDue}
                disabled={payingPastDue || paymentMethods.length === 0}
                className="bg-red-500 hover:bg-red-600"
                data-testid="pay-now-btn"
              >
                {payingPastDue ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Processing...
                  </>
                ) : (
                  "Pay Now & Restore Access"
                )}
              </Button>
              {paymentMethods.length === 0 && (
                <p className="text-xs text-muted-foreground mt-2">
                  Please add a payment method below to make a payment.
                </p>
              )}
            </div>
          </div>
        </motion.div>
      )}

      {/* Suspended Banner */}
      {isSuspended && (
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="p-4 bg-red-500/10 border border-red-500/30 rounded-sm"
        >
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-6 h-6 text-red-400 shrink-0 mt-0.5" />
            <div>
              <h3 className="font-semibold text-red-400 mb-1">Account Suspended</h3>
              <p className="text-sm text-muted-foreground">
                Your account has been suspended due to non-payment. Please contact support to reactivate your account.
              </p>
            </div>
          </div>
        </motion.div>
      )}

      {/* Current Plan Card */}
      <Card className="bg-card border-border">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="text-xl font-serif flex items-center gap-2">
            <CreditCard className="w-5 h-5 text-primary" />
            Current Plan
          </CardTitle>
          <Badge className={`${statusBadge.className} border`}>
            {statusBadge.label}
          </Badge>
        </CardHeader>
        <CardContent>
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-6">
            <div>
              <h2 className="text-3xl font-serif mb-2" data-testid="current-plan-name">
                {getCurrentPlanDisplay().name}
              </h2>
              <p className="text-2xl text-muted-foreground" data-testid="current-plan-price">
                ${getCurrentPlanDisplay().price}
                <span className="text-base">/month</span>
              </p>
              {accountStatus?.next_billing_date && subscription?.subscription_status === "active" && (
                <p className="text-sm text-muted-foreground mt-2">
                  Next billing: {new Date(accountStatus.next_billing_date).toLocaleDateString()}
                </p>
              )}
              {/* Show plan contract snapshot date if available */}
              {planContract?.snapshot_at && (
                <p className="text-xs text-muted-foreground mt-1">
                  Contract as of: {new Date(planContract.snapshot_at).toLocaleDateString()}
                </p>
              )}
            </div>

            <div className="flex gap-3">
              <Link to="/pricing">
                <Button variant="outline" data-testid="change-plan-btn">
                  Change Plan
                </Button>
              </Link>
              {/* Cancel button for active subscriptions or trials */}
              {(subscription?.subscription_status === "active" || subscription?.subscription_status === "trialing") && 
               !subscription?.cancel_at_period_end && (
                <Button 
                  variant="ghost" 
                  className="text-red-400 hover:text-red-300"
                  onClick={handleCancelSubscription}
                  disabled={canceling}
                  data-testid="cancel-subscription-btn"
                >
                  {canceling ? <Loader2 className="w-4 h-4 animate-spin" /> : "Cancel Subscription"}
                </Button>
              )}
            </div>
          </div>

          {/* Pending Tier Change Notice */}
          {hasPendingChange() && (
            <div className="mt-6 p-4 bg-blue-500/10 border border-blue-500/20 rounded-sm">
              <div className="flex items-center gap-2">
                <Clock className="w-4 h-4 text-blue-400" />
                <span className="text-sm font-medium text-blue-400">
                  {getPendingChangeInfo()?.message}
                </span>
              </div>
              <p className="text-sm text-muted-foreground mt-1">
                {getPendingChangeInfo()?.type === 'downgrade' 
                  ? "Your current plan features remain active until the end of your billing period."
                  : "Complete payment to activate your upgraded plan."}
              </p>
            </div>
          )}

          {/* Trial Progress */}
          {subscription?.subscription_status === "trialing" && (
            <div className="mt-6 p-4 bg-blue-500/10 border border-blue-500/20 rounded-sm">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-blue-400" />
                  <span className="text-sm font-medium">Trial Period</span>
                </div>
                <span className="text-sm text-muted-foreground">
                  {subscription.trial_days_remaining} days remaining
                </span>
              </div>
              <Progress value={trialProgress} className="h-2" />
              <p className="text-sm text-muted-foreground mt-2">
                {paymentMethods.length > 0 
                  ? "Your card will be charged automatically when your trial ends."
                  : "Add a payment method to continue using 360Lumina after your trial."}
              </p>
            </div>
          )}

          {/* Cancel Notice with Reactivate Option */}
          {subscription?.cancel_at_period_end && (
            <div className="mt-6 p-4 bg-yellow-500/10 border border-yellow-500/20 rounded-sm">
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div className="flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 text-yellow-400" />
                  <span className="text-sm">
                    Your subscription will end on {new Date(subscription.current_period_end).toLocaleDateString()}.
                    You&apos;ll maintain access until then.
                  </span>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleReactivateSubscription}
                  disabled={reactivating}
                  data-testid="reactivate-subscription-btn"
                >
                  {reactivating ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    "Reactivate Subscription"
                  )}
                </Button>
              </div>
            </div>
          )}

          {/* Past Due Notice */}
          {subscription?.subscription_status === "past_due" && !isRestricted && (
            <div className="mt-6 p-4 bg-red-500/10 border border-red-500/20 rounded-sm">
              <div className="flex items-center gap-2">
                <AlertCircle className="w-4 h-4 text-red-400" />
                <span className="text-sm">
                  Payment failed. Please update your payment method to continue using 360Lumina.
                </span>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Payment Methods Card */}
      <Card className="bg-card border-border">
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="text-xl font-serif flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-primary" />
              Payment Methods
            </CardTitle>
            <CardDescription>
              Manage your payment methods for automatic billing
            </CardDescription>
          </div>
          {!showAddCard && (
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => setShowAddCard(true)}
              data-testid="add-payment-method-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Card
            </Button>
          )}
        </CardHeader>
        <CardContent>
          {showAddCard && (
            <div className="mb-6 p-4 border border-border rounded-sm bg-background/30">
              <h3 className="text-sm font-medium mb-4">Add New Payment Method</h3>
              <Elements stripe={stripePromise}>
                <PaymentMethodForm 
                  onSuccess={handleCardAdded}
                  onCancel={() => setShowAddCard(false)}
                />
              </Elements>
            </div>
          )}

          {paymentMethods.length > 0 ? (
            <div className="space-y-3">
              {paymentMethods.map((pm) => (
                <div 
                  key={pm.id}
                  className={`flex items-center justify-between p-4 border rounded-sm ${
                    pm.is_default ? "border-primary/50 bg-primary/5" : "border-border"
                  }`}
                  data-testid={`payment-method-${pm.last4}`}
                >
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-9 bg-muted rounded flex items-center justify-center">
                      <span className="text-xs font-bold">{getCardBrandIcon(pm.brand)}</span>
                    </div>
                    <div>
                      <p className="font-medium flex items-center gap-2">
                        •••• •••• •••• {pm.last4}
                        {pm.is_default && (
                          <Badge variant="outline" className="text-xs">Default</Badge>
                        )}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        Expires {pm.exp_month}/{pm.exp_year}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {!pm.is_default && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleSetDefaultCard(pm.id)}
                        data-testid={`set-default-${pm.last4}`}
                      >
                        Set Default
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="icon"
                      className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                      onClick={() => handleDeleteCard(pm.id)}
                      disabled={deletingCard === pm.id}
                      data-testid={`delete-card-${pm.last4}`}
                    >
                      {deletingCard === pm.id ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Trash2 className="w-4 h-4" />
                      )}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            !showAddCard && (
              <div className="text-center py-8">
                <CreditCard className="w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50" />
                <p className="text-muted-foreground mb-2">No payment method on file</p>
                <p className="text-sm text-muted-foreground mb-4">
                  Add a card to enable automatic subscription payments
                </p>
                <Button 
                  variant="outline"
                  onClick={() => setShowAddCard(true)}
                >
                  <Plus className="w-4 h-4 mr-2" />
                  Add Payment Method
                </Button>
              </div>
            )
          )}
        </CardContent>
      </Card>

      {/* Features Card */}
      <Card className="bg-card border-border">
        <CardHeader>
          <CardTitle className="text-xl font-serif flex items-center gap-2">
            <Shield className="w-5 h-5 text-primary" />
            Your Features
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {Object.entries(features).map(([key, feature]) => (
              <div
                key={key}
                className={`p-4 rounded-sm border ${
                  feature.enabled 
                    ? "bg-green-500/5 border-green-500/20" 
                    : "bg-muted/30 border-border"
                }`}
              >
                <div className="flex items-start gap-3">
                  {feature.enabled ? (
                    <Check className="w-5 h-5 text-green-400 shrink-0 mt-0.5" />
                  ) : (
                    <div className="w-5 h-5 rounded-full border-2 border-muted-foreground/30 shrink-0 mt-0.5" />
                  )}
                  <div>
                    <p className={`font-medium ${!feature.enabled && "text-muted-foreground"}`}>
                      {feature.name}
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      {feature.description}
                    </p>
                  </div>
                </div>
                
                {!feature.enabled && (
                  <Link to="/pricing" className="inline-flex items-center gap-1 text-xs text-primary mt-3 hover:underline">
                    Upgrade to unlock <ArrowUpRight className="w-3 h-3" />
                  </Link>
                )}
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Plan Comparison */}
      <Card className="bg-card border-border">
        <CardContent className="p-6">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div>
              <h3 className="text-lg font-medium mb-1">Need more features?</h3>
              <p className="text-muted-foreground text-sm">
                Upgrade your plan to unlock all features and grow your business.
              </p>
            </div>
            <Link to="/pricing">
              <Button className="btn-primary rounded-sm" data-testid="view-plans-btn">
                View All Plans <ArrowUpRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default BillingPage;
